// =============================================================================
// Priority Spel V3 — Game configuration
// -----------------------------------------------------------------------------
// Central, tweakable rules for the game economy. In the frontend-first build the
// mock backend (src/services/mock) reads these to simulate server behaviour.
// When the real backend lands, these values move server-side; keep the SHAPES so
// the API contract stays stable.  Briefing refs in comments (§ = PDF section).
// =============================================================================

// --- Priority tiers → speelbeurten per week (§2) -----------------------------
// NOTE: the real source of a player's tier is not yet decided (delivered via the
// orderKey/JWE, or a VodafoneZiggo API). Until then it is a stub; `DEFAULT_TIER`
// is used and the mapping below is authoritative for turns-per-week.
export const TIERS = {
  zilver: { key: 'zilver', label: 'Zilver', turnsPerWeek: 1 },
  goud: { key: 'goud', label: 'Goud', turnsPerWeek: 2 },
  platinum: { key: 'platinum', label: 'Platinum', turnsPerWeek: 3 },
  diamant: { key: 'diamant', label: 'Diamant', turnsPerWeek: 4 },
}
export const DEFAULT_TIER = 'diamant' // stub until the real tier source is wired

// --- Cycle & week model (§2, §12, §16) ---------------------------------------
export const CYCLE = {
  weeks: 7, // one cycle = 7 speelweken
  bonusStarOnRejoin: 1, // §11 automatic bonus star on rejoining a new cycle
}

// --- Wheel / reel outcomes (§4) ----------------------------------------------
// The reel can land on a minigame, "Extra spel" or "Extra sterren". The system
// (server/mock) decides the outcome; specials are capped at once per week and
// excluded once obtained. Minigames have equal chance and never repeat twice in
// a row (§4.1). `extraSterrenAmount` is configurable (§4.3).
export const WHEEL = {
  extraSpel: {
    key: 'extra_spel',
    label: 'Extra spel',
    maxPerWeek: 1, // §4.2
  },
  extraSterren: {
    key: 'extra_sterren',
    label: 'Extra sterren',
    maxPerWeek: 1, // §4.3
    amount: 2, // §4.3 — always 2 stars, matches the "Extra sterren" reel badge
  },
  // Relative weighting when specials are still available. Minigames always share
  // equal probability among themselves (§4.1 "gelijke kans").
  weights: {
    minigame: 8,
    extra_spel: 1,
    extra_sterren: 1,
  },
}

// --- Stars per minigame (§7) -------------------------------------------------
// Players earn 0–3 stars, resolved centrally (resolveStars) so the balance is
// server-authoritative. `type: 'score'` maps a numeric score via ascending
// thresholds; `type: 'attempts'` maps fewer attempts → more stars.
//
// IMPORTANT: the score games (match3/timing) render their OWN result screen, which
// shows stars self-computed from the points via the game's `data/tuning.js`
// `stars.thresholds`. To keep the star the player SEES equal to the star awarded
// centrally, these rules MIRROR each game's own thresholds (single source: the
// game's tuning). No `minGuaranteed`: like the delivered games, a score below the
// first threshold earns 0 stars (the game shows its "net niet genoeg" copy).
// If the game's tuning thresholds change, update these to match.
function scoreStarRules([t1, t2, t3]) {
  return {
    type: 'score',
    minGuaranteed: 0,
    thresholds: [
      { stars: 1, min: t1 },
      { stars: 2, min: t2 },
      { stars: 3, min: t3 },
    ],
  }
}

// --- Minigame registry (§4.1, §5) --------------------------------------------
// The 4 minigames are built by another developer as Vue components. Here we keep
// only the metadata the shell needs: reel appearance + the "Speluitleg" content
// (title/body/tip/legend) + score→star rules. Copy is taken verbatim from Figma.
// The actual game component is resolved by the registry (src/games/registry.js),
// which maps each `id` below to the vendored game's adapter — keep the ids in sync.
export const MINIGAMES = [
  {
    id: 'match3',
    name: 'Priority Match',
    segmentColor: 'var(--c-seg-olive)',
    uitleg: {
      title: 'Speluitleg',
      body: 'Verwissel twee aangrenzende objecten om een rij van 3 of meer gelijke objecten te vormen.',
      tip: 'Grotere rijen en combinaties leveren meer punten op.',
    },
    // Mirrors Sterren swipen's data/tuning.js `stars.thresholds = [600, 2000, 3600]`.
    starRules: scoreStarRules([600, 2000, 3600]),
  },
  {
    id: 'unblock',
    name: 'Voetbalpuzzel',
    segmentColor: 'var(--c-seg-olive)',
    uitleg: {
      title: 'Speluitleg',
      body: 'Schuif de blokken opzij en maak de weg vrij. Breng het blok met de voetbal naar het doel om te scoren.',
      tip: null,
    },
    // The delivered puzzle reports `hints` used (not a score): fewer tips → more
    // stars. This is an attempts-shaped rule; the thresholds mirror the game's own
    // stars.byHints = [3, 2, 2, 1] (0 tips → 3★, 1–2 → 2★, 3+ → 1★) so stars stay
    // resolved centrally without inventing new values. See src/games/adapters/unblock.vue.
    starRules: {
      type: 'attempts',
      minGuaranteed: 1,
      thresholds: [
        { stars: 3, maxAttempts: 0 }, // 0 tips
        { stars: 2, maxAttempts: 2 }, // 1–2 tips
        { stars: 1, maxAttempts: Infinity }, // 3+ tips
      ],
    },
  },
  {
    id: 'timing',
    name: 'Sterren Vangen',
    segmentColor: 'var(--c-seg-navy)',
    uitleg: {
      title: 'Speluitleg',
      body: 'Klik op het juiste moment om sterren te vangen en punten te verdienen. Vraagtekens geven een verrassende bonus of effect.',
      tip: null,
    },
    // Mirrors Sterrenvangen's data/tuning.js `stars.thresholds = [400, 2000, 3000]`.
    starRules: scoreStarRules([400, 2000, 3000]),
  },
  {
    id: 'wordle',
    name: 'Woordraadsel',
    segmentColor: 'var(--c-seg-cream)',
    uitleg: {
      title: 'Speluitleg',
      body: 'Raad het woord in zo weinig mogelijk pogingen.',
      tip: null,
      legend: [
        { state: 'correct', label: 'De letter staat goed' },
        { state: 'present', label: 'Letter op de verkeerde plek' },
        { state: 'absent', label: 'De letter komt niet voor' },
      ],
    },
    starRules: {
      type: 'attempts',
      minGuaranteed: 1,
      // fewer attempts → more stars (§ example on the Wordle score screen)
      thresholds: [
        { stars: 3, maxAttempts: 4 }, // 1–4 pogingen
        { stars: 2, maxAttempts: 6 }, // 5–6 pogingen
        { stars: 1, maxAttempts: Infinity }, // 7+ pogingen
      ],
    },
  },
]

// Resolve stars for a finished game result given its config.
export function resolveStars(rules, result) {
  if (!rules) return 0
  if (rules.type === 'attempts') {
    const attempts = result?.attempts ?? Infinity
    const hit = rules.thresholds.find((t) => attempts <= t.maxAttempts)
    return Math.max(hit ? hit.stars : 0, result?.completed ? rules.minGuaranteed : 0)
  }
  // default: score-based
  const score = result?.score ?? 0
  let stars = 0
  for (const t of rules.thresholds) {
    if (score >= t.min) stars = Math.max(stars, t.stars)
  }
  return Math.max(stars, result?.completed ? rules.minGuaranteed : 0)
}
